// Phase 9B — useAgentWorkload hook.
//
// Read-only consumer of agents, agent_tasks, and workload_snapshots
// tables. Fetches workload data for the authenticated user.
//
// Trust contract:
//   - Read via RLS-scoped SELECT. Writes via RPCs only.
//   - Defensive parsing — missing fields render with fallbacks.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export type AgentRole =
  | "lead_analyst"
  | "market_analyst"
  | "risk_analyst"
  | "data_analyst"
  | "automation_agent"
  | "review_agent";

export type TaskStatus =
  | "unassigned"
  | "assigned"
  | "in_progress"
  | "blocked"
  | "completed"
  | "cancelled";

export type TaskPriority = "critical" | "high" | "medium" | "low";

export interface Agent {
  id: string;
  name: string;
  role: AgentRole;
  avatar_icon: string;
  active: boolean;
  capacity: number;
  meta: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AgentTask {
  id: string;
  agent_id: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  board_type: string | null;
  board_item_id: string | null;
  effort_points: number;
  due_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  tags: readonly string[];
  meta: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface WorkloadSnapshot {
  id: string;
  agent_id: string;
  snapshot_date: string;
  tasks_assigned: number;
  tasks_completed: number;
  tasks_blocked: number;
  utilization_pct: number;
  created_at: string;
}

// ─── State interfaces ──────────────────────────────────────────

export interface UseAgentsState {
  agents: readonly Agent[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export interface UseAgentTasksState {
  tasks: readonly AgentTask[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Parsers ───────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseAgent(row: Record<string, unknown>): Agent {
  return {
    id: String(row.id ?? ""),
    name: String(row.name ?? ""),
    role: (row.role as AgentRole) ?? "market_analyst",
    avatar_icon: String(row.avatar_icon ?? "◈"),
    active: row.active !== false,
    capacity: asNum(row.capacity),
    meta: typeof row.meta === "object" && row.meta !== null
      ? (row.meta as Record<string, unknown>)
      : {},
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

function parseTask(row: Record<string, unknown>): AgentTask {
  return {
    id: String(row.id ?? ""),
    agent_id: asStr(row.agent_id),
    title: String(row.title ?? ""),
    description: asStr(row.description),
    status: (row.status as TaskStatus) ?? "unassigned",
    priority: (row.priority as TaskPriority) ?? "medium",
    board_type: asStr(row.board_type),
    board_item_id: asStr(row.board_item_id),
    effort_points: asNum(row.effort_points),
    due_at: asStr(row.due_at),
    started_at: asStr(row.started_at),
    completed_at: asStr(row.completed_at),
    tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
    meta: typeof row.meta === "object" && row.meta !== null
      ? (row.meta as Record<string, unknown>)
      : {},
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

// ─── useAgents ─────────────────────────────────────────────────

export function useAgents(): UseAgentsState {
  const { user } = useAuthSession();
  const [agents, setAgents] = useState<readonly Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setAgents([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from("agents" as never) as unknown as {
          select: (cols: string) => {
            order: (col: string, opts: { ascending: boolean }) => Promise<{
              data: Record<string, unknown>[] | null;
              error: { message: string } | null;
            }>;
          };
        }
      )
        .select("*")
        .order("created_at", { ascending: false });

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setAgents([]);
        setLoading(false);
        return;
      }

      setAgents(
        (resp.data ?? []).filter((r): r is Record<string, unknown> => r !== null).map(parseAgent),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, tick]);

  return useMemo(
    () => ({ agents, loading, error, refetch }),
    [agents, loading, error, refetch],
  );
}

// ─── useAgentTasks ─────────────────────────────────────────────

export function useAgentTasks(
  statusFilter?: TaskStatus,
): UseAgentTasksState {
  const { user } = useAuthSession();
  const [tasks, setTasks] = useState<readonly AgentTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setTasks([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      let resp: {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };

      if (statusFilter) {
        resp = await (
          client.from("agent_tasks" as never) as unknown as {
            select: (cols: string) => {
              eq: (col: string, val: string) => {
                order: (col: string, opts: { ascending: boolean }) => Promise<{
                  data: Record<string, unknown>[] | null;
                  error: { message: string } | null;
                }>;
              };
            };
          }
        )
          .select("*")
          .eq("status", statusFilter)
          .order("created_at", { ascending: false });
      } else {
        resp = await (
          client.from("agent_tasks" as never) as unknown as {
            select: (cols: string) => {
              order: (col: string, opts: { ascending: boolean }) => Promise<{
                data: Record<string, unknown>[] | null;
                error: { message: string } | null;
              }>;
            };
          }
        )
          .select("*")
          .order("created_at", { ascending: false });
      }

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setTasks([]);
        setLoading(false);
        return;
      }

      setTasks(
        (resp.data ?? []).filter((r): r is Record<string, unknown> => r !== null).map(parseTask),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, statusFilter, tick]);

  return useMemo(
    () => ({ tasks, loading, error, refetch }),
    [tasks, loading, error, refetch],
  );
}
